import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, Calendar, Info, AlertCircle, CheckCircle, Clock, Trash2, Check } from 'lucide-react';
interface Notification {
  id: string;
  title: string;
  message: string;
  date: string;
  type: 'info' | 'warning' | 'success';
  read: boolean;
}
const initialNotifications: Notification[] = [{
  id: '1',
  title: 'Нагадування про членський внесок',
  message: 'Ваш щорічний членський внесок у розмірі 500 грн очікує на сплату. Будь ласка, сплатіть до 31 січня 2025 року, щоб зберегти статус активного члена.',
  date: '2 години тому',
  type: 'warning',
  read: false
}, {
  id: '2',
  title: 'Відкрито реєстрацію на Чемпіонат Києва',
  message: 'Розпочато прийом заявок на участь у Чемпіонаті Києва зі скелелазіння (дисципліна трудність). Змагання відбудуться 15-16 лютого в спорткомплексі НАУ.',
  date: 'Вчора, 14:30',
  type: 'info',
  read: false
}, {
  id: '3',
  title: 'Оновлення статусу профілю',
  message: 'Ваші дані про спортивний розряд (КМС) успішно підтверджено адміністратором федерації.',
  date: '12 січня 2025',
  type: 'success',
  read: true
}, {
  id: '4',
  title: 'Зміна в розкладі тренувань',
  message: 'Зверніть увагу: тренування збірної команди перенесено з суботи на неділю (10:00).',
  date: '10 січня 2025',
  type: 'info',
  read: true
}];
export function NotificationsPanel() {
  const [notifications, setNotifications] = useState<Notification[]>(initialNotifications);
  const markAsRead = (id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? {
      ...n,
      read: true
    } : n));
  };
  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({
      ...n,
      read: true
    })));
  };
  const deleteNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };
  const unreadCount = notifications.filter(n => !n.read).length;
  const getIcon = (type: Notification['type']) => {
    switch (type) {
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-orange-500" data-id="element-347" />;
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" data-id="element-348" />;
      default:
        return <Info className="w-5 h-5 text-blue-500" data-id="element-349" />;
    }
  };
  const getBgColor = (type: Notification['type']) => {
    switch (type) {
      case 'warning':
        return 'bg-orange-50';
      case 'success':
        return 'bg-green-50';
      default:
        return 'bg-blue-50';
    }
  };
  return <div className="space-y-6" data-id="element-350">
      {/* Header */}
      <div className="bg-white rounded-2xl p-6 shadow-[0_4px_24px_rgba(0,0,0,0.04)] border border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-351">
        <div data-id="element-352">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2" data-id="element-353">
            <Bell className="w-6 h-6 text-slate-400" data-id="element-354" />
            Сповіщення
            {unreadCount > 0 && <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-orange-100 text-orange-600" data-id="element-355">
                {unreadCount} нових
              </span>}
          </h2>
          <p className="text-slate-500 text-sm mt-1" data-id="element-356">
            Важливі повідомлення від федерації та клубу
          </p>
        </div>

        {unreadCount > 0 && <button onClick={markAllAsRead} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors self-start sm:self-auto" data-id="element-357">
            <Check className="w-4 h-4" data-id="element-358" />
            Позначити всі як прочитані
          </button>}
      </div>

      {/* List */}
      <div className="space-y-4" data-id="element-359">
        <AnimatePresence mode="popLayout" data-id="element-360">
          {notifications.length > 0 ? notifications.map(notification => <motion.div key={notification.id} layout initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className={`relative group bg-white rounded-2xl p-5 shadow-sm border transition-all ${notification.read ? 'border-slate-100' : 'border-blue-100 shadow-[0_4px_20px_rgba(59,143,212,0.08)]'}`} data-id="element-361">
                <div className="flex gap-4" data-id="element-362">
                  {/* Icon */}
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${getBgColor(notification.type)}`} data-id="element-363">
                    {getIcon(notification.type)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0" data-id="element-364">
                    <div className="flex items-start justify-between gap-4" data-id="element-365">
                      <div data-id="element-366">
                        <h3 className={`text-base font-semibold mb-1 ${notification.read ? 'text-slate-700' : 'text-slate-900'}`} data-id="element-367">
                          {notification.title}
                        </h3>
                        <p className="text-slate-600 text-sm leading-relaxed" data-id="element-368">
                          {notification.message}
                        </p>
                      </div>

                      {/* Actions (Desktop) */}
                      <div className="hidden sm:flex items-center gap-2" data-id="element-369">
                        {!notification.read && <button onClick={() => markAsRead(notification.id)} className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors" title="Позначити як прочитане" data-id="element-370">
                            <div className="w-2 h-2 rounded-full bg-blue-500" data-id="element-371" />
                          </button>}
                        <button onClick={() => deleteNotification(notification.id)} className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors" title="Видалити" data-id="element-372">
                          <Trash2 className="w-4 h-4" data-id="element-373" />
                        </button>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center gap-4 mt-3" data-id="element-374">
                      <div className="flex items-center gap-1.5 text-xs text-slate-400" data-id="element-375">
                        <Clock className="w-3.5 h-3.5" data-id="element-376" />
                        {notification.date}
                      </div>
                      {!notification.read && <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full" data-id="element-377">
                          Нове
                        </span>}
                    </div>
                  </div>
                </div>

                {/* Mobile Actions Overlay (visible on tap/focus if needed, but here simplified) */}
                <div className="sm:hidden flex justify-end gap-3 mt-4 pt-3 border-t border-slate-50" data-id="element-378">
                  {!notification.read && <button onClick={() => markAsRead(notification.id)} className="text-xs font-medium text-blue-600 py-1 px-2" data-id="element-379">
                      Прочитати
                    </button>}
                  <button onClick={() => deleteNotification(notification.id)} className="text-xs font-medium text-slate-400 py-1 px-2" data-id="element-380">
                    Видалити
                  </button>
                </div>
              </motion.div>) : <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="text-center py-12 bg-white rounded-2xl border border-slate-100 border-dashed" data-id="element-381">
              <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-382">
                <Bell className="w-8 h-8 text-slate-300" data-id="element-383" />
              </div>
              <h3 className="text-lg font-medium text-slate-900" data-id="element-384">
                Немає нових сповіщень
              </h3>
              <p className="text-slate-500 text-sm mt-1" data-id="element-385">
                Ми повідомимо вас про важливі події
              </p>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}